/**
 * Applied as every Clarity dialog's `panelClass`.
 *
 * The isolation seam, not decoration. `ui-kit-docs` loads `ui-styles`' legacy
 * `material/dialog.override.scss` globally, and the docs app carries `.ds` on `<html>`, so those 743
 * lines reach any `mat-dialog-container` the app renders — `background-color: transparent` on the
 * container among them. Editing that stylesheet is out of this story's diff, so the dialog isolates
 * itself instead, in three layers:
 *
 * 1. geometry is stated in `MatDialogConfig` (`width` / `maxWidth` / `maxHeight`), which lands as an
 *    inline style and therefore outranks every selector in that file;
 * 2. `ClarityDialogFrameComponent` paints its own surface, so the legacy container declarations have
 *    nothing left to affect;
 * 3. this class, which scopes the rules that reach inside the Material container without raising
 *    specificity against a line the rest of `design-schedule` depends on.
 *
 * Layer 3 carries the height bound: the frame's stylesheet hands the pane's `maxHeight` to the surface
 * (the legacy override pins it to `98dvh`) and lets the body component's host shrink, so only the
 * frame's body scrolls. Component SCSS never reaches this workspace's test bed, so what a spec *can*
 * prove, and does, is that the class and the bound reach the pane.
 */
export const CLARITY_DIALOG_PANEL_CLASS = 'eff-clarity-dialog';

/**
 * Added beside {@link CLARITY_DIALOG_PANEL_CLASS} on a `passive` dialog's pane. That pane is a bare CDK
 * overlay with no `.mat-mdc-dialog-surface` inside, so the frame's stylesheet paints the pane itself
 * under this class — the same surface the Material backend draws, so both backends look alike.
 */
export const CLARITY_PASSIVE_DIALOG_PANEL_CLASS = 'eff-clarity-dialog--passive';

/**
 * Added beside {@link CLARITY_DIALOG_PANEL_CLASS} on a dialog opened at the `media` width. Under it the
 * frame publishes `--eff-clarity-dialog-frame-body-max-block-size`, the block size its body has left
 * inside the dialog's height bound, for content that must fit without scrolling.
 */
export const CLARITY_MEDIA_DIALOG_PANEL_CLASS = 'eff-clarity-dialog--media';
